import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, MessageSquare, Video, FileText, Clock, Shield, Smartphone } from 'lucide-react';
import { mockHowItWorks } from '../mockData';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
            How TouchScreenMD Works
          </h1>
          <p className="text-xl text-[#5F6C7B] max-w-3xl mx-auto mb-8">
            Getting quality healthcare has never been easier. Connect with licensed providers in three simple steps.
          </p>
          <Button
            size="lg"
            className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
          >
            Start a Visit Now
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {mockHowItWorks.map((item, index) => (
              <div key={item.id} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] mb-6">
                    <span className="text-3xl font-bold text-white">{item.step}</span>
                  </div>
                  <h2 className="text-4xl font-bold text-[#12324A] mb-6">{item.title}</h2>
                  <p className="text-lg text-[#5F6C7B] leading-relaxed mb-6">{item.description}</p>
                  <ul className="space-y-3">
                    {index === 0 && (
                      <>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Simple online form takes less than 2 minutes</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Describe your symptoms or health concern</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Provide relevant medical history</span>
                        </li>
                      </>
                    )}
                    {index === 1 && (
                      <>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Secure, HIPAA-aligned video connection</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Board-certified, licensed providers</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Average wait time under 10 minutes</span>
                        </li>
                      </>
                    )}
                    {index === 2 && (
                      <>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Personalized treatment recommendations</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Prescriptions sent directly to your pharmacy</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={20} />
                          <span className="text-[#5F6C7B]">Follow-up support when needed</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="bg-gradient-to-br from-[#F4F6F8] to-white rounded-2xl p-12 border border-gray-200 shadow-lg">
                    <div className="aspect-square bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] rounded-2xl flex items-center justify-center">
                      {index === 0 && <MessageSquare className="text-white" size={80} />}
                      {index === 1 && <Video className="text-white" size={80} />}
                      {index === 2 && <FileText className="text-white" size={80} />}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Why Choose TouchScreenMD?
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              We make healthcare accessible, affordable, and convenient
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300">
              <Clock className="text-[#1FB6B2] mb-4" size={40} />
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">24/7 Availability</h3>
              <p className="text-[#5F6C7B]">Get care whenever you need it, day or night, weekends and holidays</p>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300">
              <Shield className="text-[#1FB6B2] mb-4" size={40} />
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Secure & Private</h3>
              <p className="text-[#5F6C7B]">HIPAA-aligned platform with strong safeguards to protect your information</p>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300">
              <Smartphone className="text-[#1FB6B2] mb-4" size={40} />
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Easy Access</h3>
              <p className="text-[#5F6C7B]">Connect from your phone, tablet, or computer—no app download required</p>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300">
              <CheckCircle className="text-[#1FB6B2] mb-4" size={40} />
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Licensed Providers</h3>
              <p className="text-[#5F6C7B]">All providers are board-certified and licensed in your state</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience Better Healthcare?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Start your visit now and connect with a licensed provider in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
            >
              Start a Visit Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
